// Realistic Social Media Post Templates
import React from 'react';
import {
  Heart, MessageCircle, Share2, Repeat2, Bookmark, MoreHorizontal,
  ThumbsUp, Send, Eye, CheckCircle, Globe, BarChart2, Play,
} from 'lucide-react';
import { useMediaLightbox, LongPressMediaTile } from './MediaLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isVideo = (url) => {
  if (!url) return false;
  const ext = url.split('?')[0].split('.').pop().toLowerCase();
  return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext);
};

const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

// V14.13.9 — Tap/long-press opens fullscreen lightbox.
// `mediaList` allows multi-image posts to swipe through all attachments
// from the lightbox; defaults to `[url]` so the existing call sites keep
// working without change.
const PostMedia = ({ url, className = 'w-full h-full object-cover', controls = false, autoPlay = false, mediaList }) => {
  const list = mediaList && mediaList.length > 0 ? mediaList : [url].filter(Boolean);
  const { open, Lightbox } = useMediaLightbox();
  const resolved = resolveUrl(url);

  const Inner = isVideo(url) ? (
    <div className="relative w-full h-full">
      <video src={resolved} className={className} controls={controls} autoPlay={autoPlay} muted loop playsInline preload="metadata" />
      {!controls && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center"><Play className="w-6 h-6 text-white ml-1" /></div>
        </div>
      )}
    </div>
  ) : (
    <img src={resolved} alt="" className={className} />
  );

  // When the underlying media has native controls (i.e. user is playing the
  // video), we DON'T wrap in LongPressMediaTile — otherwise tapping the play
  // button would open the lightbox. Long-press still works via the parent.
  if (isVideo(url) && controls) {
    return <>{Inner}{Lightbox}</>;
  }

  return (
    <>
      <LongPressMediaTile
        onActivate={(i) => open(list, list.indexOf(url) >= 0 ? list.indexOf(url) : i)}
        className="w-full h-full"
      >
        {Inner}
      </LongPressMediaTile>
      {Lightbox}
    </>
  );
};

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toString() || '0';
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'Just now';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString();
};

export const TwitterTemplate = ({ post, onInteract }) => {
  const { author, content, engagement, timestamp, verification_status, media_urls } = post;
  const handleClick = (type) => { if (onInteract) onInteract(post.post_id, type); };
  return (
    <div className="bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          {author?.avatar ? <img src={author.avatar} alt="" className="w-10 h-10 rounded-full" /> : (
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center"><span className="text-white font-bold text-sm">{author?.name?.charAt(0).toUpperCase()}</span></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-bold text-gray-900 dark:text-white truncate">{author?.name}</span>
            {verification_status === 'verified' && <CheckCircle className="w-4 h-4 text-blue-500 fill-blue-500" />}
            <span className="text-gray-500">@{author?.handle}</span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-500">{formatTimeAgo(timestamp)}</span>
            <button className="ml-auto text-gray-500 hover:text-blue-500 p-1 rounded-full"><MoreHorizontal className="w-5 h-5" /></button>
          </div>
          <p className="text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{content}</p>
          {media_urls?.length > 0 && (
            <div className="mt-3 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 max-h-80">
              <PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full max-h-80 object-cover" controls={isVideo(media_urls[0])} />
            </div>
          )}
          <div className="flex items-center justify-between mt-3 max-w-md">
            <button onClick={() => handleClick('comment')} className="flex items-center gap-1 text-gray-500 hover:text-blue-500 group"><div className="p-2 rounded-full group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20"><MessageCircle className="w-4 h-4" /></div><span className="text-sm">{formatNumber(engagement?.comments)}</span></button>
            <button onClick={() => handleClick('retweet')} className="flex items-center gap-1 text-gray-500 hover:text-green-500 group"><div className="p-2 rounded-full group-hover:bg-green-50 dark:group-hover:bg-green-900/20"><Repeat2 className="w-4 h-4" /></div><span className="text-sm">{formatNumber(engagement?.retweets || engagement?.shares)}</span></button>
            <button onClick={() => handleClick('like')} className="flex items-center gap-1 text-gray-500 hover:text-pink-500 group"><div className="p-2 rounded-full group-hover:bg-pink-50 dark:group-hover:bg-pink-900/20"><Heart className="w-4 h-4" /></div><span className="text-sm">{formatNumber(engagement?.likes)}</span></button>
            <button onClick={() => handleClick('view')} className="flex items-center gap-1 text-gray-500 hover:text-blue-500 group"><div className="p-2 rounded-full group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20"><BarChart2 className="w-4 h-4" /></div><span className="text-sm">{formatNumber(engagement?.views)}</span></button>
            <button className="text-gray-500 hover:text-blue-500 p-2 rounded-full"><Bookmark className="w-4 h-4" /></button>
            <button onClick={() => handleClick('share')} className="text-gray-500 hover:text-blue-500 p-2 rounded-full"><Share2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FacebookTemplate = ({ post, onInteract }) => {
  const { author, content, title, engagement, timestamp, verification_status, media_urls, location } = post;
  const handleClick = (type) => { if (onInteract) onInteract(post.post_id, type); };
  return (
    <div className="bg-white dark:bg-[#242526] rounded-lg shadow">
      <div className="p-4 pb-0">
        <div className="flex items-start gap-3">
          {author?.avatar ? <img src={author.avatar} alt="" className="w-10 h-10 rounded-full" /> : (
            <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center"><span className="text-white font-bold text-sm">{author?.name?.charAt(0).toUpperCase()}</span></div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-1"><span className="font-semibold text-gray-900 dark:text-white">{author?.name}</span>{verification_status === 'verified' && <CheckCircle className="w-4 h-4 text-[#1877F2] fill-[#1877F2]" />}</div>
            <div className="flex items-center gap-1 text-xs text-gray-500"><span>{formatTimeAgo(timestamp)}</span><span>·</span><Globe className="w-3 h-3" />{location && <span>· {location}</span>}</div>
          </div>
          <button className="text-gray-500 p-2 rounded-full"><MoreHorizontal className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="px-4 py-2">{title && <p className="font-semibold text-lg text-gray-900 dark:text-white mb-1">{title}</p>}<p className="text-gray-900 dark:text-white whitespace-pre-wrap">{content}</p></div>
      {media_urls?.length > 0 && <div className="mt-1"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full" controls={isVideo(media_urls[0])} /></div>}
      <div className="px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1"><div className="flex -space-x-1"><div className="w-5 h-5 rounded-full bg-[#1877F2] flex items-center justify-center"><ThumbsUp className="w-3 h-3 text-white fill-white" /></div><div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center"><Heart className="w-3 h-3 text-white fill-white" /></div></div><span className="text-sm text-gray-500 ml-1">{formatNumber(engagement?.likes)}</span></div>
        <div className="flex gap-3 text-sm text-gray-500"><span>{formatNumber(engagement?.comments)} comments</span><span>{formatNumber(engagement?.shares)} shares</span></div>
      </div>
      <div className="px-2 py-1 flex items-center">
        <button onClick={() => handleClick('like')} className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md font-semibold"><ThumbsUp className="w-5 h-5" /><span>Like</span></button>
        <button onClick={() => handleClick('comment')} className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md font-semibold"><MessageCircle className="w-5 h-5" /><span>Comment</span></button>
        <button onClick={() => handleClick('share')} className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md font-semibold"><Share2 className="w-5 h-5" /><span>Share</span></button>
      </div>
    </div>
  );
};

export const InstagramTemplate = ({ post, onInteract, onOpen }) => {
  const { author, content, engagement, timestamp, verification_status, media_urls } = post;
  const handleClick = (type) => { if (onInteract) onInteract(post.post_id, type); };
  const openDetail = (e) => { if (onOpen) { e.stopPropagation(); onOpen(); } };
  const isLong = (content?.length || 0) > 120;
  const clickableCard = typeof onOpen === 'function';
  return (
    <div
      className={`bg-white dark:bg-black border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${clickableCard ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={clickableCard ? openDetail : undefined}
      role={clickableCard ? 'button' : undefined}
      tabIndex={clickableCard ? 0 : undefined}
      onKeyDown={clickableCard ? (e) => { if (e.key === 'Enter' || e.key === ' ') openDetail(e); } : undefined}
      data-testid={clickableCard ? `instagram-card-open-${post.post_id}` : undefined}
    >
      <div className="p-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full p-[2px] bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600">
          <div className="w-full h-full rounded-full bg-white dark:bg-black p-[2px]">
            {author?.avatar ? <img src={author.avatar} alt="" className="w-full h-full rounded-full" /> : (
              <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"><span className="text-white font-bold text-xs">{author?.name?.charAt(0).toUpperCase()}</span></div>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0"><div className="flex items-center gap-1"><span className="font-semibold text-sm truncate">{author?.handle || author?.name}</span>{verification_status === 'verified' && <CheckCircle className="w-3 h-3 text-[#0095F6] fill-[#0095F6] flex-shrink-0" />}</div>{post.location && <p className="text-xs text-gray-500 truncate">{post.location}</p>}</div>
        <button className="flex-shrink-0"><MoreHorizontal className="w-5 h-5" /></button>
      </div>
      {media_urls?.length > 0 ? (
        <div className="w-full max-h-72 overflow-hidden bg-gray-100 dark:bg-gray-900"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full h-full object-cover" controls={isVideo(media_urls[0])} /></div>
      ) : (
        <div className="w-full h-48 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center p-4">
          <p className="text-white text-center font-medium text-sm line-clamp-4">{content}</p>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4"><Heart onClick={(e) => { e.stopPropagation(); handleClick('like'); }} className="w-5 h-5 cursor-pointer" /><MessageCircle onClick={(e) => { e.stopPropagation(); handleClick('comment'); }} className="w-5 h-5 cursor-pointer" /><Send onClick={(e) => { e.stopPropagation(); handleClick('share'); }} className="w-5 h-5 cursor-pointer" /></div>
          <Bookmark className="w-5 h-5 cursor-pointer" />
        </div>
        <p className="font-semibold text-sm mb-1">{formatNumber(engagement?.likes)} likes</p>
        <p className="text-sm line-clamp-2"><span className="font-semibold">{author?.handle || author?.name}</span> {content}</p>
        {isLong && clickableCard && (
          <button type="button" onClick={openDetail} className="text-xs text-gray-500 hover:text-foreground mt-0.5 font-medium" data-testid={`instagram-more-${post.post_id}`}>
            … more
          </button>
        )}
        {engagement?.comments > 0 && <p className="text-sm text-gray-500 mt-1 cursor-pointer">View all {formatNumber(engagement?.comments)} comments</p>}
        <p className="text-[10px] text-gray-400 uppercase mt-2">{formatTimeAgo(timestamp)}</p>
      </div>
    </div>
  );
};

export const LinkedInTemplate = ({ post, onInteract }) => {
  const { author, content, engagement, timestamp, verification_status, media_urls } = post;
  const handleClick = (type) => { if (onInteract) onInteract(post.post_id, type); };
  return (
    <div className="bg-white dark:bg-[#1D2226] rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-4 pb-0">
        <div className="flex items-start gap-3">
          {author?.avatar ? <img src={author.avatar} alt="" className="w-12 h-12 rounded-full" /> : (
            <div className="w-12 h-12 rounded-full bg-[#0A66C2] flex items-center justify-center"><span className="text-white font-bold">{author?.name?.charAt(0).toUpperCase()}</span></div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-1"><span className="font-semibold text-gray-900 dark:text-white">{author?.name}</span>{verification_status === 'verified' && <CheckCircle className="w-4 h-4 text-[#0A66C2] fill-[#0A66C2]" />}</div>
            <p className="text-xs text-gray-500 line-clamp-1">{author?.bio || 'Professional'}</p>
            <p className="text-xs text-gray-500 flex items-center gap-1"><span>{formatTimeAgo(timestamp)}</span><span>·</span><Globe className="w-3 h-3" /></p>
          </div>
        </div>
      </div>
      <div className="px-4 py-3"><p className="text-gray-900 dark:text-white whitespace-pre-wrap">{content}</p></div>
      {media_urls?.length > 0 && <PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full" controls={isVideo(media_urls[0])} />}
      <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1"><div className="flex -space-x-1"><div className="w-4 h-4 rounded-full bg-[#0A66C2] flex items-center justify-center"><ThumbsUp className="w-2 h-2 text-white fill-white" /></div></div><span>{formatNumber(engagement?.likes)}</span></div>
        <div className="flex gap-2"><span>{formatNumber(engagement?.comments)} comments</span><span>·</span><span>{formatNumber(engagement?.shares)} reposts</span></div>
      </div>
      <div className="px-2 py-1 flex">
        <button onClick={() => handleClick('like')} className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded font-semibold text-sm"><ThumbsUp className="w-5 h-5" /><span>Like</span></button>
        <button onClick={() => handleClick('comment')} className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded font-semibold text-sm"><MessageCircle className="w-5 h-5" /><span>Comment</span></button>
        <button onClick={() => handleClick('repost')} className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded font-semibold text-sm"><Repeat2 className="w-5 h-5" /><span>Repost</span></button>
        <button onClick={() => handleClick('share')} className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded font-semibold text-sm"><Send className="w-5 h-5" /><span>Send</span></button>
      </div>
    </div>
  );
};

export const TikTokTemplate = ({ post, onInteract, onOpen }) => {
  const { author, content, engagement, verification_status, media_urls } = post;
  const handleClick = (type) => { if (onInteract) onInteract(post.post_id, type); };
  const openDetail = (e) => { if (onOpen) { e.stopPropagation(); onOpen(); } };
  const isLong = (content?.length || 0) > 100;
  const clickableCard = typeof onOpen === 'function';
  return (
    <div
      className={`bg-black rounded-lg overflow-hidden relative ${clickableCard ? 'cursor-pointer' : ''}`}
      style={{ aspectRatio: '9/14', maxHeight: '350px' }}
      onClick={clickableCard ? openDetail : undefined}
      role={clickableCard ? 'button' : undefined}
      tabIndex={clickableCard ? 0 : undefined}
      onKeyDown={clickableCard ? (e) => { if (e.key === 'Enter' || e.key === ' ') openDetail(e); } : undefined}
      data-testid={clickableCard ? `tiktok-card-open-${post.post_id}` : undefined}
    >
      {media_urls?.length > 0 ? (
        isVideo(media_urls[0]) ? <video src={resolveUrl(media_urls[0])} className="w-full h-full object-cover absolute inset-0" autoPlay muted loop playsInline preload="metadata" /> : <img src={resolveUrl(media_urls[0])} alt="" className="w-full h-full object-cover absolute inset-0" />
      ) : (
        <div className="w-full h-full bg-gradient-to-b from-gray-900 to-black absolute inset-0 flex items-center justify-center p-6"><p className="text-white text-center text-lg font-medium line-clamp-6">{content}</p></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5">
        <div className="flex flex-col items-center"><div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"><Heart onClick={(e) => { e.stopPropagation(); handleClick('like'); }} className="w-6 h-6 text-white" /></div><span className="text-white text-xs mt-1">{formatNumber(engagement?.likes)}</span></div>
        <div className="flex flex-col items-center"><div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"><MessageCircle onClick={(e) => { e.stopPropagation(); handleClick('comment'); }} className="w-6 h-6 text-white" /></div><span className="text-white text-xs mt-1">{formatNumber(engagement?.comments)}</span></div>
        <div className="flex flex-col items-center"><div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"><Share2 onClick={(e) => { e.stopPropagation(); handleClick('share'); }} className="w-6 h-6 text-white" /></div><span className="text-white text-xs mt-1">{formatNumber(engagement?.shares)}</span></div>
      </div>
      <div className="absolute bottom-4 left-3 right-14">
        <div className="flex items-center gap-2 mb-2"><span className="text-white font-bold text-sm">@{author?.handle || author?.name}</span>{verification_status === 'verified' && <CheckCircle className="w-3.5 h-3.5 text-[#20D5EC] fill-[#20D5EC]" />}</div>
        <p className="text-white text-xs line-clamp-2">{content}</p>
        {isLong && clickableCard && (
          <button type="button" onClick={openDetail} className="text-white/80 text-[11px] mt-0.5 hover:text-white font-medium" data-testid={`tiktok-more-${post.post_id}`}>
            … more
          </button>
        )}
        {post.hashtags?.length > 0 && <p className="text-white/80 text-xs mt-1">{post.hashtags.map(h => `#${h}`).join(' ')}</p>}
      </div>
    </div>
  );
};

export const RedditTemplate = ({ post }) => {
  const { author, content, engagement, timestamp, media_urls } = post;
  return (
    <div className="bg-white dark:bg-[#1A1A1B] border border-gray-300 dark:border-gray-700 rounded-lg">
      <div className="flex">
        <div className="w-10 flex flex-col items-center py-3 bg-gray-50 dark:bg-[#161617] rounded-l-lg">
          <button className="text-gray-400 hover:text-orange-500"><svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 3l-7 7h4v7h6v-7h4z" /></svg></button>
          <span className="text-xs font-bold text-gray-900 dark:text-white my-1">{formatNumber(engagement?.likes)}</span>
          <button className="text-gray-400 hover:text-blue-500"><svg className="w-5 h-5 rotate-180" viewBox="0 0 20 20" fill="currentColor"><path d="M10 3l-7 7h4v7h6v-7h4z" /></svg></button>
        </div>
        <div className="flex-1 p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center"><span className="text-white text-[10px] font-bold">r/</span></div>
            <span className="font-bold text-gray-900 dark:text-white">r/{post.hashtags?.[0] || 'general'}</span>
            <span>· u/{author?.handle || author?.name}</span>
            <span>· {formatTimeAgo(timestamp)}</span>
          </div>
          {post.title && <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">{post.title}</h3>}
          <p className="text-sm text-gray-800 dark:text-gray-300 mb-3">{content}</p>
          {media_urls?.length > 0 && <div className="rounded-lg overflow-hidden mb-3 max-h-64"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full h-full object-cover" controls={isVideo(media_urls[0])} /></div>}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <button className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded"><MessageCircle className="w-4 h-4" />{formatNumber(engagement?.comments)} Comments</button>
            <button className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded"><Share2 className="w-4 h-4" />Share</button>
            <button className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded"><Bookmark className="w-4 h-4" />Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TelegramTemplate = ({ post }) => {
  const { author, content, engagement, timestamp, media_urls } = post;
  return (
    <div className="bg-white dark:bg-[#212121] rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-3 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center"><span className="text-white font-bold">{author?.name?.charAt(0).toUpperCase()}</span></div>
        <div className="flex-1"><p className="font-semibold text-sm">{author?.name || 'Channel'}</p><p className="text-xs text-gray-500">{formatNumber(engagement?.views)} views · {formatTimeAgo(timestamp)}</p></div>
      </div>
      {media_urls?.length > 0 && <div className="max-h-56 overflow-hidden"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full h-full object-cover" controls={isVideo(media_urls[0])} /></div>}
      <div className="p-3"><p className="text-sm whitespace-pre-wrap">{content}</p>{post.hashtags?.length > 0 && <p className="text-sm text-blue-500 mt-2">{post.hashtags.map(h => `#${h}`).join(' ')}</p>}</div>
      <div className="px-3 pb-3 flex items-center gap-3 text-gray-400"><span className="text-xs">{formatTimeAgo(timestamp)}</span><span className="text-xs ml-auto">{formatNumber(engagement?.views)} views</span></div>
    </div>
  );
};

export const WeiboTemplate = ({ post }) => {
  const { author, content, engagement, timestamp, verification_status, media_urls } = post;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex gap-3">
        <div className="w-12 h-12 rounded-full bg-[#E6162D] flex items-center justify-center"><span className="text-white font-bold">{author?.name?.charAt(0).toUpperCase()}</span></div>
        <div className="flex-1">
          <div className="flex items-center gap-1"><span className="font-bold text-[#E6162D]">{author?.name}</span>{verification_status === 'verified' && <span className="bg-orange-500 text-white text-[10px] px-1 rounded">V</span>}</div>
          <p className="text-xs text-gray-500">{formatTimeAgo(timestamp)}</p>
        </div>
      </div>
      <p className="mt-3">{content}</p>
      {media_urls?.length > 0 && <div className="mt-3 rounded-lg overflow-hidden"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full max-h-60 object-cover" controls={isVideo(media_urls[0])} /></div>}
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-around text-gray-500 text-sm">
        <button className="flex items-center gap-1 hover:text-[#E6162D]"><Repeat2 className="w-4 h-4" />{formatNumber(engagement?.shares)}</button>
        <button className="flex items-center gap-1 hover:text-[#E6162D]"><MessageCircle className="w-4 h-4" />{formatNumber(engagement?.comments)}</button>
        <button className="flex items-center gap-1 hover:text-[#E6162D]"><Heart className="w-4 h-4" />{formatNumber(engagement?.likes)}</button>
      </div>
    </div>
  );
};

export const WhatsAppTemplate = ({ post }) => {
  const { author, content, timestamp, media_urls } = post;
  const time = timestamp ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '12:00';
  const colors = ['#25D366', '#34B7F1', '#FF6B6B', '#FFB347', '#9B59B6', '#E74C3C'];
  const nameColor = colors[Math.abs((author?.name || '').charCodeAt(0) || 0) % colors.length];

  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'300\' height=\'300\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'60\' height=\'60\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1\' fill=\'%23ffffff08\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect fill=\'%23091715\' width=\'300\' height=\'300\'/%3E%3Crect fill=\'url(%23p)\' width=\'300\' height=\'300\'/%3E%3C/svg%3E")', backgroundColor: '#091715' }}>
      {/* Group header */}
      <div className="bg-[#1F2C34] flex items-center gap-3 px-3 py-2 border-b border-[#0B141A]">
        <div className="w-8 h-8 rounded-full bg-[#2A3942] flex items-center justify-center">
          <span className="text-gray-400 text-xs font-bold">{author?.name?.charAt(0)?.toUpperCase() || 'G'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-200 truncate">{author?.name || 'Group Chat'}</p>
          <p className="text-[10px] text-gray-500">tap here for group info</p>
        </div>
      </div>

      {/* Chat area */}
      <div className="p-3 space-y-1 min-h-[120px]">
        {/* System message */}
        <div className="flex justify-center mb-2">
          <span className="bg-[#182229] text-[10px] text-gray-400 px-3 py-1 rounded-lg">
            {timestamp ? new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Today'}
          </span>
        </div>

        {/* Message bubble */}
        <div className="flex justify-start">
          <div className="max-w-[85%] bg-[#1F2C34] rounded-lg rounded-tl-none shadow-sm overflow-hidden relative">
            {/* Tail */}
            <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden">
              <div className="w-3 h-3 bg-[#1F2C34] rotate-45 transform origin-bottom-right" />
            </div>
            {media_urls?.length > 0 && (
              <div className="max-h-48 overflow-hidden">
                <PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full h-full object-cover" controls={isVideo(media_urls[0])} />
              </div>
            )}
            <div className="px-2.5 pt-1.5 pb-1">
              <p className="text-xs font-medium mb-0.5" style={{ color: nameColor }}>{author?.name || 'Contact'}</p>
              <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap">{content}</p>
              <div className="flex items-center justify-end gap-1 mt-0.5 -mb-0.5">
                <span className="text-[10px] text-gray-500">{time}</span>
                {/* Double check marks */}
                <svg width="16" height="11" viewBox="0 0 16 11" className="text-[#53bdeb]">
                  <path d="M11.071 0.653a0.457 0.457 0 0 0-0.304-0.149 0.501 0.501 0 0 0-0.356 0.110l-5.783 5.518-0.038 0.038-0.038 0.038c-0.076 0.076-0.076 0.190 0 0.266l0.077 0.077 0.115-0.115c0.038-0.038 0.076-0.076 0.076-0.076l5.783-5.518a0.501 0.501 0 0 0 0.149-0.356 0.457 0.457 0 0 0-0.149-0.304z" fill="currentColor"/>
                  <path d="M15.071 0.653a0.457 0.457 0 0 0-0.304-0.149 0.501 0.501 0 0 0-0.356 0.110l-5.783 5.518-0.038 0.038-0.038 0.038c-0.076 0.076-0.076 0.190 0 0.266l0.077 0.077 0.115-0.115c0.038-0.038 0.076-0.076 0.076-0.076l5.783-5.518a0.501 0.501 0 0 0 0.149-0.356 0.457 0.457 0 0 0-0.149-0.304z" fill="currentColor"/>
                  <path d="M5.486 7.403l-2.838-2.352a0.501 0.501 0 0 0-0.356-0.110 0.457 0.457 0 0 0-0.304 0.149 0.457 0.457 0 0 0-0.149 0.304 0.501 0.501 0 0 0 0.149 0.356l3.210 2.664c0.076 0.076 0.190 0.076 0.266 0l0.077-0.077-0.038-0.038-0.038-0.038z" fill="currentColor"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Input bar */}
      <div className="bg-[#1F2C34] flex items-center gap-2 px-3 py-2">
        <div className="flex-1 bg-[#2A3942] rounded-full px-4 py-1.5">
          <span className="text-xs text-gray-500">Type a message</span>
        </div>
        <div className="w-8 h-8 rounded-full bg-[#00A884] flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.239 1.816-13.239 1.817z"/></svg>
        </div>
      </div>
    </div>
  );
};

export const VKTemplate = ({ post }) => {
  const { author, content, engagement, timestamp, media_urls } = post;
  return (
    <div className="bg-white dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-3 flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center"><span className="text-white font-bold text-sm">{author?.name?.charAt(0).toUpperCase()}</span></div><div className="flex-1"><p className="font-semibold text-sm">{author?.name}</p><p className="text-xs text-gray-500">{formatTimeAgo(timestamp)}</p></div></div>
      <div className="px-3 pb-3"><p className="text-sm">{content}</p></div>
      {media_urls?.length > 0 && <div className="max-h-64 overflow-hidden"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full h-full object-cover" controls={isVideo(media_urls[0])} /></div>}
      <div className="p-3 flex items-center justify-between text-gray-500 text-xs border-t border-gray-100 dark:border-gray-700">
        <button className="flex items-center gap-1"><Heart className="w-4 h-4" />{formatNumber(engagement?.likes)}</button>
        <button className="flex items-center gap-1"><MessageCircle className="w-4 h-4" />{formatNumber(engagement?.comments)}</button>
        <button className="flex items-center gap-1"><Share2 className="w-4 h-4" />{formatNumber(engagement?.shares)}</button>
        <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{formatNumber(engagement?.views)}</span>
      </div>
    </div>
  );
};

export const NewsTemplate = ({ post, platformColor = '#000', onOpen }) => {
  const { author, content, title, timestamp, media_urls } = post;
  const clickable = typeof onOpen === 'function';
  const handleActivate = clickable ? (e) => { e.stopPropagation(); onOpen(); } : undefined;
  return (
    <div
      className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow ${clickable ? 'cursor-pointer' : ''}`}
      onClick={handleActivate}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleActivate(e); } : undefined}
      data-testid={clickable ? `news-card-open-${post.post_id}` : undefined}
    >
      {media_urls?.length > 0 && <div className="w-full h-40 bg-gray-100 dark:bg-gray-800 overflow-hidden"><PostMedia url={media_urls[0]} mediaList={media_urls} className="w-full h-full object-cover" controls={isVideo(media_urls[0])} /></div>}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: platformColor }}>{post.platform?.charAt(0).toUpperCase()}</div>
          <span className="text-xs font-semibold uppercase truncate" style={{ color: platformColor }}>{post.platform}</span>
          <span className="text-xs text-gray-500 flex-shrink-0">· {formatTimeAgo(timestamp)}</span>
        </div>
        {title && <h3 className="font-bold text-base text-gray-900 dark:text-white mb-2 line-clamp-2 hover:text-blue-600 leading-tight">{title}</h3>}
        <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 leading-relaxed">{content}</p>
        {clickable && (content?.length > 140 || (title && title.length > 60)) && (
          <button
            type="button"
            onClick={handleActivate}
            className="mt-2 text-xs font-semibold hover:underline focus:outline-none"
            style={{ color: platformColor }}
            data-testid={`news-card-read-more-${post.post_id}`}
          >
            Read more →
          </button>
        )}
        {author?.name && <p className="mt-3 text-xs text-gray-500">By {author.name}</p>}
      </div>
    </div>
  );
};

export const SocialPostTemplate = ({ post, platform, onInteract, onOpen }) => {
  const platformName = platform?.toLowerCase() || post.platform?.toLowerCase();
  const socialPlatforms = ['twitter', 'facebook', 'instagram', 'linkedin', 'tiktok', 'threads', 'weibo', 'wechat', 'vk', 'qq', 'douyin', 'whatsapp', 'telegram', 'line', 'kakaotalk', 'reddit', 'koo', 'sharechat'];
  const newsPlatforms = ['bbc', 'cnn', 'reuters', 'aljazeera', 'nytimes', 'guardian', 'foxnews', 'rt', 'xinhua', 'regional', 'news'];

  if (platformName === 'twitter' || platformName === 'threads') return <TwitterTemplate post={post} onInteract={onInteract} />;
  if (platformName === 'facebook') return <FacebookTemplate post={post} onInteract={onInteract} />;
  if (platformName === 'instagram') return <InstagramTemplate post={post} onInteract={onInteract} onOpen={onOpen} />;
  if (platformName === 'linkedin') return <LinkedInTemplate post={post} onInteract={onInteract} />;
  if (platformName === 'tiktok' || platformName === 'douyin') return <TikTokTemplate post={post} onInteract={onInteract} onOpen={onOpen} />;
  if (platformName === 'reddit') return <RedditTemplate post={post} />;
  if (platformName === 'telegram') return <TelegramTemplate post={post} />;
  if (platformName === 'whatsapp') return <WhatsAppTemplate post={post} />;
  if (platformName === 'vk' || platformName === 'ok') return <VKTemplate post={post} />;
  if (platformName === 'weibo' || platformName === 'xiaohongshu' || platformName === 'bilibili') return <WeiboTemplate post={post} />;
  if (newsPlatforms.includes(platformName)) {
    const colors = { bbc: '#BB1919', cnn: '#CC0000', reuters: '#FF8000', aljazeera: '#D4AA00', nytimes: '#000000', guardian: '#052962', foxnews: '#003366', rt: '#5EAA22', xinhua: '#D52B1E' };
    return <NewsTemplate post={post} platformColor={colors[platformName] || '#000'} onOpen={onOpen} />;
  }
  if (socialPlatforms.includes(platformName)) return <TwitterTemplate post={post} onInteract={onInteract} />;
  return <NewsTemplate post={post} onOpen={onOpen} />;
};

export const isSocialPlatform = (platformName) => {
  const name = platformName?.toLowerCase();
  return ['twitter', 'facebook', 'instagram', 'linkedin', 'tiktok', 'threads', 'weibo', 'wechat', 'vk', 'qq', 'douyin', 'whatsapp', 'telegram', 'line', 'kakaotalk', 'reddit', 'koo', 'sharechat', 'xiaohongshu', 'bilibili', 'ok', 'naver'].includes(name);
};

export const isNewsPlatform = (platformName) => {
  const name = platformName?.toLowerCase();
  return ['bbc', 'cnn', 'reuters', 'aljazeera', 'nytimes', 'guardian', 'foxnews', 'rt', 'xinhua', 'regional', 'news'].includes(name);
};

export default SocialPostTemplate;
